import { Popover } from '@base-ui/react/popover';
import { Info } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface SourcePillNote {
  label: string;
  description: string;
}

export interface SourcePillProps {
  /* Where what is on this page comes from, e.g. "Outlook-agenda". */
  children: string;
  /* Names the panel, and follows the pill's own text in the pill's name. */
  infoLabel: string;
  /* What the panel says, one short paragraph per point. */
  notes: readonly SourcePillNote[];
  className?: string;
}

/**
 * Says where a page's contents came from, and opens the two sentences behind
 * that.
 *
 * A label with a popover, not an icon with a tooltip. "Where does this data
 * come from, and what does Wundo write back into my calendar?" is the first
 * question anyone asks of an agenda, and a tooltip does not exist for whoever
 * does not hover — which is everyone on a phone. So the pill itself is the
 * control, and it reads as text rather than as a glyph waiting to be found.
 *
 * It explains and operates nothing. Nothing in the panel changes what the page
 * shows; the filter beside it does that.
 */
export function SourcePill({ children, infoLabel, notes, className }: SourcePillProps) {
  return (
    <Popover.Root>
      <Popover.Trigger
        data-slot='source-pill'
        aria-label={`${children}, ${infoLabel}`}
        className={cn(
          'flex cursor-pointer items-center gap-1.75 rounded-full bg-fill py-2 pr-3.5 pl-3',
          'text-sm font-semibold text-ink-muted transition hover:bg-line hover:text-ink',
          'data-popup-open:bg-line data-popup-open:text-ink',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
          className,
        )}
      >
        {/* The connection is live — the same green that means Wundo elsewhere. */}
        <span className='size-1.5 shrink-0 rounded-full bg-accent' aria-hidden='true' />
        {children}
        <Info className='size-3 shrink-0' aria-hidden='true' />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side='bottom' align='end' sideOffset={8} className='z-50'>
          <Popover.Popup
            data-slot='source-pill-detail'
            aria-label={infoLabel}
            className='w-85 rounded-md border border-line bg-surface p-4 shadow-menu outline-none animate-pop'
          >
            {notes.map((note, index) => (
              <div
                key={note.label}
                className={cn(index > 0 && 'mt-3.5 border-t border-fill pt-3.5')}
              >
                <p className='text-base font-semibold text-ink'>{note.label}</p>
                <p className='mt-0.75 text-base text-ink-muted'>{note.description}</p>
              </div>
            ))}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
